import { Component, OnInit, computed, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { ApiConfiguration } from '../../core/api/api-configuration';
import { AlertService } from '../../shared/services/alert.service';
import { Editor, editorConfig } from '../../shared/ckeditor/ckeditor-config';
import { Utils } from '../../core/utils';

interface PatientNotesModel {
  ID?: number;
  Notes?: string;
}

@Component({
  selector: 'app-notes',
  templateUrl: './notes.component.html',
  styleUrl: './notes.component.css',
  standalone: false,
})
export class NotesComponent implements OnInit {
  notes = signal('');
  readonly maxNotesLength = 100000;
  readonly maxNotesHtmlLength = 2 * 1024 * 1024;
  readonly notesTextLength = computed(() => this.getNotesTextLength(this.notes()));
  selectedPatientID: number = 0;
  isLoading = signal(false);
  isSaving = signal(false);
  initialNotes: string = '';

  public Editor = Editor;
  public editorConfig = editorConfig;

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
    private apiConfig: ApiConfiguration,
    private alertService: AlertService
  ) {}

  ngOnInit(): void {
    this.route.parent?.params.subscribe((params) => {
      this.selectedPatientID = +params['patientID'];
      this.loadPatientNotes();
    });
  }

  private loadPatientNotes(): void {
    if (!this.selectedPatientID) {
      return;
    }

    this.isLoading.set(true);
    const url = `${this.apiConfig.rootUrl}/api/Supervisor/GetPatientNotes`;
    this.http
      .get<PatientNotesModel>(url, {
        params: {
          patientID: this.selectedPatientID,
        },
      })
      .subscribe({
        next: (results) => {
          this.initialNotes = results?.Notes || '';
          this.notes.set(results?.Notes || '');
          this.isLoading.set(false);
        },
        error: (error) => {
          console.error('Error loading patient notes', error);
          this.alertService.error(Utils.getAjaxError(error));
          this.isLoading.set(false);
        },
      });
  }

  onNotesChange(event: any): void {
    if (event.editor) {
      const data = event.editor.getData();
      this.notes.set(data);
    }
  }

  saveNotes(): void {
    if (!this.selectedPatientID || this.isSaving()) {
      return;
    }

    const patientID = this.selectedPatientID;
    const notes = this.notes();
    if (notes.length > this.maxNotesHtmlLength) {
      this.alertService.error('Patient notes formatting is too large. Remove pasted images or simplify formatting and try again.');
      return;
    }
    if (this.notesTextLength() > this.maxNotesLength) {
      this.alertService.error('Patient notes cannot exceed 100,000 characters.');
      return;
    }

    this.isSaving.set(true);
    const url = `${this.apiConfig.rootUrl}/api/Supervisor/SavePatientNotes`;
    this.http
      .post<void>(
        url,
        {
          ID: patientID,
          Notes: notes,
        },
        {
          params: {
            patientID: patientID,
          },
        }
      )
      .subscribe({
        next: () => {
          if (this.selectedPatientID === patientID) {
            this.alertService.success('Patient notes saved successfully');
          }
          this.isSaving.set(false);
        },
        error: (error) => {
          console.error('Error saving patient notes', error);
          if (this.selectedPatientID === patientID) {
            this.alertService.error(Utils.getAjaxError(error));
          }
          this.isSaving.set(false);
        },
      });
  }

  private getNotesTextLength(html: string): number {
    // Match the server's GetHtmlTextLength: parse HTML and count decoded text.
    const template = document.createElement('template');
    template.innerHTML = html;
    template.content.querySelectorAll('script, style').forEach((node) => node.remove());
    return (template.content.textContent || '').length;
  }
}
